export function BenefitsOfSellingAsIs() {
  const benefits = [
    {
      icon: 'ri-time-line',
      title: 'Close in 7-30 Days',
      description: 'Skip the 6-18 month renovation timeline. Get your money fast and move on with your life without the stress of managing a construction project.',
      savings: 'Save 6-18 months'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Zero Upfront Costs',
      description: 'Keep your capital in your pocket. No need to drain savings, take out loans, or risk money on renovations that may not pay off.',
      savings: 'Save $500K-$2M+'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Eliminate All Risk',
      description: 'No contractor delays, cost overruns, permit issues, or surprise problems. Transfer all renovation risk to the buyer who specializes in it.',
      savings: 'Avoid 20-40% overruns'
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Stop Carrying Costs',
      description: 'End the monthly drain of mortgage payments, taxes, insurance, and lost rental income immediately. Every month you wait costs thousands.',
      savings: 'Save $10K-$50K/month'
    },
    {
      icon: 'ri-user-smile-line',
      title: 'Zero Stress & Hassle',
      description: 'No dealing with contractors, permits, inspections, or tenant complaints during construction. Sell and be done with it.',
      savings: 'Priceless peace of mind'
    },
    {
      icon: 'ri-hand-coin-line',
      title: 'Immediate Liquidity',
      description: 'Get cash in hand quickly to reinvest, retire, or handle other financial priorities. Time value of money matters.',
      savings: 'Use capital now, not later'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-teal-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            The Powerful Benefits of Selling AS IS
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            When you sell your apartment building as-is to experienced investors like us, you unlock immediate advantages that renovating simply cannot match.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {benefits.map((benefit, index) => (
            <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-teal-100">
              <div className="w-16 h-16 bg-teal-600 rounded-xl flex items-center justify-center mb-6">
                <i className={`${benefit.icon} text-3xl text-white`}></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">{benefit.title}</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">{benefit.description}</p>
              <div className="pt-4 border-t border-teal-100">
                <span className="text-teal-600 font-bold">{benefit.savings}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Comparison Infographic */}
        <div className="bg-white rounded-2xl p-10 shadow-xl mb-12">
          <h3 className="text-3xl font-bold mb-10 text-center">Side-by-Side Timeline Comparison</h3>
          
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Remodel Timeline */}
            <div>
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center">
                  <i className="ri-hammer-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold">Remodel First Path</h4>
              </div>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">1</div>
                    <div className="w-1 h-full bg-red-200 mt-2"></div>
                  </div>
                  <div className="pb-6">
                    <div className="font-bold text-lg mb-1">Months 1-2: Planning</div>
                    <p className="text-gray-600">Hire architects, get permits, secure financing</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">2</div>
                    <div className="w-1 h-full bg-red-200 mt-2"></div>
                  </div>
                  <div className="pb-6">
                    <div className="font-bold text-lg mb-1">Months 3-4: Tenant Relocation</div>
                    <p className="text-gray-600">Negotiate with tenants, handle move-outs, lost income</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">3</div>
                    <div className="w-1 h-full bg-red-200 mt-2"></div>
                  </div>
                  <div className="pb-6">
                    <div className="font-bold text-lg mb-1">Months 5-12: Construction</div>
                    <p className="text-gray-600">Manage contractors, deal with delays and overruns</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">4</div>
                    <div className="w-1 h-full bg-red-200 mt-2"></div>
                  </div>
                  <div className="pb-6">
                    <div className="font-bold text-lg mb-1">Months 13-15: Lease-Up</div>
                    <p className="text-gray-600">Find new tenants at higher rents, stabilize occupancy</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">5</div>
                    <div className="w-1 h-full bg-red-200 mt-2"></div>
                  </div>
                  <div className="pb-6">
                    <div className="font-bold text-lg mb-1">Months 16-18: Marketing & Sale</div>
                    <p className="text-gray-600">List property, negotiate, close transaction</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">✓</div>
                  </div>
                  <div>
                    <div className="font-bold text-2xl text-red-600 mb-1">18 Months Total</div>
                    <p className="text-gray-600">Plus $1M+ invested and massive stress</p>
                  </div>
                </div>
              </div>
            </div>

            {/* AS IS Timeline */}
            <div>
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center">
                  <i className="ri-hand-coin-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold">Sell AS IS Path</h4>
              </div>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">1</div>
                    <div className="w-1 h-full bg-teal-200 mt-2"></div>
                  </div>
                  <div className="pb-6">
                    <div className="font-bold text-lg mb-1">Day 1: Contact Us</div>
                    <p className="text-gray-600">Simple phone call or form submission</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">2</div>
                    <div className="w-1 h-full bg-teal-200 mt-2"></div>
                  </div>
                  <div className="pb-6">
                    <div className="font-bold text-lg mb-1">Days 2-3: Property Review</div>
                    <p className="text-gray-600">We analyze your property and financials</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">3</div>
                    <div className="w-1 h-full bg-teal-200 mt-2"></div>
                  </div>
                  <div className="pb-6">
                    <div className="font-bold text-lg mb-1">Days 4-7: Cash Offer</div>
                    <p className="text-gray-600">Receive fair, no-obligation offer</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">4</div>
                    <div className="w-1 h-full bg-teal-200 mt-2"></div>
                  </div>
                  <div className="pb-6">
                    <div className="font-bold text-lg mb-1">Days 8-14: Due Diligence</div>
                    <p className="text-gray-600">Quick inspection and paperwork review</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">5</div>
                  </div>
                  <div>
                    <div className="font-bold text-2xl text-teal-600 mb-1">7-30 Days Total</div>
                    <p className="text-gray-600">Cash in hand, zero costs, zero stress</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Image */}
        <div className="rounded-2xl overflow-hidden shadow-xl">
          <img
            src="https://public.readdy.ai/ai/img_res/4c72046f28709b077329172e58b0707c.jpg"
            alt="Successful apartment building sale as-is"
            className="w-full h-[500px] object-cover object-top"
          />
        </div>
      </div>
    </section>
  );
}